import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiUsers } from "react-icons/fi";
import { toast } from "react-toastify";
import { useQueryClient } from "react-query";
import useCustomerById from "../../../marketplace_admin/libs/useCustomerById";
import useCustomerOrders from "../../../marketplace_admin/libs/useCustomerOrders";
import useBlockUser from "../../../marketplace_admin/libs/useBlockUser";
import useUnblockUser from "../../../marketplace_admin/libs/useUnblockUser";
import PageHeader from "../../components/PageHeader";
import DetailGrid from "../../components/DetailGrid";
import DataTable from "../../components/DataTable";
import YesNoBadge from "../../components/YesNoBadge";
import StatusBadge from "../../components/StatusBadge";
import ConfirmModal from "../../components/ConfirmModal";
import EmptyState from "../../components/EmptyState";
import Spinner from "../../../Spinner/Spinner";

const AdminCustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [modal, setModal] = useState(null);

  const { data: customer, isLoading: customerLoading, isError: customerError } = useCustomerById(id);
  const { data: orders = [], isLoading: ordersLoading } = useCustomerOrders(id);
  const blockUser = useBlockUser();
  const unblockUser = useUnblockUser();

  const invalidate = () => {
    queryClient.invalidateQueries(["customer", id]);
    queryClient.invalidateQueries("customers");
  };

  const handleBlock = async () => {
    try {
      await blockUser.mutateAsync({
        userId: id,
        blockData: {
          startDate: new Date().toISOString(),
          endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
        },
      });
      invalidate();
      toast.success("Customer blocked");
      setModal(null);
    } catch (err) {
      toast.error(err.message || "Block failed");
    }
  };

  const handleUnblock = async () => {
    try {
      await unblockUser.mutateAsync({ userId: id });
      invalidate();
      toast.success("Customer unblocked");
      setModal(null);
    } catch (err) {
      toast.error(err.message || "Unblock failed");
    }
  };

  const fullName = customer ? [customer.first_name, customer.last_name].filter(Boolean).join(" ") : "";

  const orderColumns = [
    { key: "orderNumber", label: "Order #" },
    {
      key: "date",
      label: "Date",
      render: (r) => new Date(r.createdAt).toLocaleDateString(),
    },
    {
      key: "total",
      label: "Total",
      render: (r) => `NGN ${(r.totalAmount || 0).toLocaleString()}`,
    },
    {
      key: "payment",
      label: "Payment",
      render: (r) => r.paymentStatus || "—",
    },
    {
      key: "status",
      label: "Status",
      render: (r) => <StatusBadge status={r.status} />,
    },
  ];

  return (
    <>
      <Link to="/admin/customers" className="dashboard-back-link">
        <FiArrowLeft aria-hidden="true" /> Back to customers
      </Link>

      <PageHeader
        title={fullName || "Customer details"}
        subtitle={customer?.email}
        icon={FiUsers}
        actions={
          customer && (
            customer.blocked ? (
              <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={() => setModal("unblock")}>
                Unblock
              </button>
            ) : (
              <button type="button" className="dashboard-btn dashboard-btn--danger" onClick={() => setModal("block")}>
                Block
              </button>
            )
          )
        }
      />

      {customerLoading && <Spinner />}
      {customerError && (
        <div className="dashboard-card">
          <p style={{ color: "var(--dash-danger)", margin: 0 }}>Failed to load customer.</p>
          <button type="button" className="dashboard-btn dashboard-btn--secondary" style={{ marginTop: 12 }} onClick={() => navigate("/admin/customers")}>
            Back to list
          </button>
        </div>
      )}

      {customer && (
        <>
          <div className="dashboard-card">
            <h2 className="dashboard-card__title">Profile</h2>
            <DetailGrid
              items={[
                { label: "ID", value: customer._id?.slice(0, 8).toUpperCase() },
                { label: "Name", value: fullName },
                { label: "Email", value: customer.email },
                { label: "Phone", value: [customer.country_code, customer.phone_number].filter(Boolean).join(" ") },
                { label: "Active", value: <YesNoBadge value={customer.isActive} /> },
                { label: "Blocked", value: <YesNoBadge value={customer.blocked} variant="blocked" /> },
              ]}
            />
          </div>

          <div className="dashboard-card" style={{ padding: orders.length ? "20px 0 0" : 20 }}>
            <h2 className="dashboard-card__title" style={{ padding: orders.length ? "0 20px" : 0 }}>
              Orders ({orders.length})
            </h2>
            {ordersLoading && <div style={{ padding: "0 20px 20px" }}><Spinner /></div>}
            {!ordersLoading && orders.length === 0 && (
              <div style={{ padding: "0 20px 20px" }}>
                <EmptyState title="No orders yet" message="This customer has not placed any orders." />
              </div>
            )}
            {!ordersLoading && orders.length > 0 && (
              <DataTable
                columns={orderColumns}
                rows={orders.map((o) => ({ ...o, _key: o._id }))}
                loading={false}
              />
            )}
          </div>
        </>
      )}

      <ConfirmModal
        open={modal === "block"}
        title="Block customer"
        message="This customer will not be able to sign in until unblocked."
        confirmLabel="Block"
        danger
        loading={blockUser.isLoading}
        onConfirm={handleBlock}
        onCancel={() => setModal(null)}
      />
      <ConfirmModal
        open={modal === "unblock"}
        title="Unblock customer"
        message="Restore sign-in access for this customer?"
        confirmLabel="Unblock"
        loading={unblockUser.isLoading}
        onConfirm={handleUnblock}
        onCancel={() => setModal(null)}
      />
    </>
  );
};

export default AdminCustomerDetailPage;
